import { isLikelyTransientFetchError } from '../../utils/errorUtils';
import { VUSER_BASE_URL } from '../config';
import { ApiResponse } from '../../types/api';
import type {
  Experiment,
  ExperimentScoreBreakdown,
  ExperimentTokenStatistics,
  Persona,
  Dialog,
  DialogTokenStatistics,
  Reaction,
  VirtualUser,
  ChatViewerLabelResponse,
  ChatViewerResolvedLabel,
  ChatViewerLabelUpsertBody,
  ChatViewerStepVirtualReaction,
} from '../../types/vuser';
import type {
  RagDeleteFolderResponse,
  RagDeleteResponseData,
  RagDocument,
  RagFolder,
  RagPresignResponseData,
} from '../../types/rag';
import { getCookie } from '../../utils/cookies';
import { api, createApiClient } from './client';

const vuserApi = createApiClient(VUSER_BASE_URL);

function withIntegration(integrationId: string, params?: Record<string, any>) {
  return { ...(params || {}), integration_id: integrationId };
}

function withAccessTokenAuth(headers?: Record<string, string>): Record<string, string> {
  const accessToken = getCookie('access_token');
  if (!accessToken) return headers ?? {};
  return { ...(headers ?? {}), Authorization: `Bearer ${accessToken}` };
}

// Virtual User Service endpoints (new)
export const vuser = {
  health: async (): Promise<{ status: string }> => {
    const response = await vuserApi.get<{ status: string }>('/vuser/health');
    return response.data;
  },

  // Virtual users
  createVirtualUser: async (
    integrationId: string,
    body: Pick<VirtualUser, 'type' | 'name' | 'description' | 'user_config'> & {
      ai_config?: string;
      virtual_user_system_prompt?: string | null;
      first_user_message_instruction?: string | null;
      followup_user_message_instruction?: string | null;
      memory_enabled?: boolean;
      diary_enabled?: boolean;
      portrait_enabled?: boolean;
    }
  ): Promise<ApiResponse<{ id: string; type: VirtualUser['type']; name: string }>> => {
    const response = await vuserApi.post<ApiResponse>('/vuser/virtual-users', body, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  listVirtualUsers: async (integrationId: string): Promise<ApiResponse<VirtualUser[]>> => {
    const response = await vuserApi.get<ApiResponse<VirtualUser[]>>('/vuser/virtual-users', {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  getVirtualUser: async (integrationId: string, id: string): Promise<ApiResponse<VirtualUser>> => {
    const response = await vuserApi.get<ApiResponse<VirtualUser>>(`/vuser/virtual-users/${id}`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  updateVirtualUser: async (
    integrationId: string,
    id: string,
    body: Partial<
      Pick<
        VirtualUser,
        | 'name'
        | 'description'
        | 'user_config'
        | 'ai_config'
        | 'virtual_user_system_prompt'
        | 'first_user_message_instruction'
        | 'followup_user_message_instruction'
        | 'memory_enabled'
        | 'diary_enabled'
        | 'portrait_enabled'
      >
    >
  ): Promise<ApiResponse<VirtualUser>> => {
    const response = await vuserApi.put<ApiResponse<VirtualUser>>(`/vuser/virtual-users/${id}`, body, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  deleteVirtualUser: async (integrationId: string, id: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await vuserApi.delete<ApiResponse<{ message: string }>>(`/vuser/virtual-users/${id}`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  setPersona: async (
    integrationId: string,
    id: string,
    persona_id: string
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await vuserApi.put<ApiResponse<{ message: string }>>(
      `/vuser/virtual-users/${id}/persona`,
      { persona_id },
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  // Personas
  createPersona: async (
    integrationId: string,
    body: Omit<Persona, 'id' | 'integration_id' | 'created_at' | 'updated_at'>
  ): Promise<ApiResponse<Persona>> => {
    const response = await vuserApi.post<ApiResponse<Persona>>('/vuser/personas', body, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  listPersonas: async (integrationId: string, virtual_user_id: string): Promise<ApiResponse<Persona[]>> => {
    const response = await vuserApi.get<ApiResponse<Persona[]>>('/vuser/personas', {
      params: withIntegration(integrationId, { virtual_user_id }),
    });
    return response.data;
  },

  getPersona: async (integrationId: string, id: string): Promise<ApiResponse<Persona>> => {
    const response = await vuserApi.get<ApiResponse<Persona>>(`/vuser/personas/${id}`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  updatePersona: async (
    integrationId: string,
    id: string,
    body: Partial<Pick<Persona, 'name' | 'description' | 'reaction_rules' | 'judge_meta_prompts'>>
  ): Promise<ApiResponse<Persona>> => {
    const response = await vuserApi.put<ApiResponse<Persona>>(`/vuser/personas/${id}`, body, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  deletePersona: async (integrationId: string, id: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await vuserApi.delete<ApiResponse<{ message: string }>>(`/vuser/personas/${id}`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  // Dialogs
  generateDialog: async (
    integrationId: string,
    body: {
      virtual_user_id: string;
      persona_id: string;
      title: string;
      initial_message: string;
      iterations?: number;
      config?: string;
    }
  ): Promise<ApiResponse<Dialog>> => {
    const response = await vuserApi.post<ApiResponse<Dialog>>('/vuser/dialogs/generate', body, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  listDialogs: async (integrationId: string, virtual_user_id?: string): Promise<ApiResponse<Dialog[]>> => {
    const response = await vuserApi.get<ApiResponse<Dialog[]>>('/vuser/dialogs', {
      params: withIntegration(integrationId, virtual_user_id ? { virtual_user_id } : undefined),
    });
    return response.data;
  },

  getDialog: async (integrationId: string, id: string): Promise<ApiResponse<Dialog>> => {
    const response = await vuserApi.get<ApiResponse<Dialog>>(`/vuser/dialogs/${id}`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  getDialogStatistics: async (integrationId: string, id: string): Promise<ApiResponse<DialogTokenStatistics>> => {
    const response = await vuserApi.get<ApiResponse<DialogTokenStatistics>>(`/vuser/dialogs/${id}/statistics`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  deleteDialog: async (integrationId: string, id: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await vuserApi.delete<ApiResponse<{ message: string }>>(`/vuser/dialogs/${id}`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  // Reactions
  reactionsByDialog: async (integrationId: string, dialog_id: string): Promise<ApiResponse<Reaction[]>> => {
    const response = await vuserApi.get<ApiResponse<Reaction[]>>(`/vuser/reactions/dialog/${dialog_id}`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },
  reactionsByMessage: async (integrationId: string, message_id: string): Promise<ApiResponse<Reaction[]>> => {
    const response = await vuserApi.get<ApiResponse<Reaction[]>>(`/vuser/reactions/message/${message_id}`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },
  reactionsByVirtualUser: async (integrationId: string, virtual_user_id: string): Promise<ApiResponse<Reaction[]>> => {
    const response = await vuserApi.get<ApiResponse<Reaction[]>>(`/vuser/reactions/virtual-user/${virtual_user_id}`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  /**
   * Стейты активного воркфлоу интеграции — список для выбора стартовой позиции
   * эксперимента. Отсортирован бэкендом, порядок стабилен между запросами.
   *
   * 404 «integration has no active workflow» — не ошибка интерфейса: на такой
   * интеграции выбирать не из чего. Обрабатывать отдельно от прочих сбоев.
   */
  getWorkflowStates: async (integrationId: string): Promise<import('../../types/vuser').WorkflowStatesSnapshot> => {
    const response = await api.get<ApiResponse<import('../../types/vuser').WorkflowStatesSnapshot>>(
      `/training/integrations/${encodeURIComponent(integrationId)}/workflow-states`
    );
    const payload = response.data as ApiResponse<import('../../types/vuser').WorkflowStatesSnapshot>;
    if (!payload?.success || !payload.data) {
      throw new Error(payload?.error?.message || 'Failed to load workflow states');
    }
    const data = payload.data;
    // Контракт обещает массив, но доверять этому нельзя: неполный ответ ронял
    // всю страницу на `states.length`. Нормализуем на границе, а не в разметке.
    return {
      ...data,
      states: Array.isArray(data.states)
        ? data.states
            .filter((state) => typeof state?.state_id === 'string')
            .map((state) => ({
              state_id: state.state_id,
              coordinator_flags_required: Array.isArray(state.coordinator_flags_required)
                ? state.coordinator_flags_required
                : [],
            }))
        : [],
    };
  },

  // Experiments
  listExperiments: async (integrationId: string): Promise<ApiResponse<Experiment[]>> => {
    const response = await vuserApi.get<ApiResponse<Experiment[]>>('/vuser/experiments', {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  startExperiment: async (
    integrationId: string,
    body: Experiment['config'] & {
      experiment_name?: string;
      train_ratio?: number;
      validation_ratio?: number;
      limit_history?: boolean;
      history_depth?: number | null;
      reaction_weights?: import('../../types/reactionWeights').ReactionWeights;
    }
  ): Promise<ApiResponse<Experiment>> => {
    const response = await vuserApi.post<ApiResponse<Experiment>>('/vuser/experiments/start', body, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  getExperiment: async (integrationId: string, id: string): Promise<ApiResponse<Experiment>> => {
    const response = await vuserApi.get<ApiResponse<Experiment>>(`/vuser/experiments/${id}`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  cancelExperiment: async (integrationId: string, id: string): Promise<ApiResponse<{ status: string }>> => {
    const response = await vuserApi.post<ApiResponse<{ status: string }>>(
      `/vuser/experiments/${id}/cancel`,
      {},
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  /** Creates a new experiment from a failed/cancelled one; returns the new experiment. */
  retryExperiment: async (experimentId: string, integrationId: string): Promise<ApiResponse<Experiment>> => {
    const response = await vuserApi.post<ApiResponse<Experiment>>(
      `/vuser/experiments/${encodeURIComponent(experimentId)}/retry`,
      {},
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  deleteExperiment: async (integrationId: string, id: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await vuserApi.delete<ApiResponse<{ message: string }>>(`/vuser/experiments/${id}`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  scoreExperiment: async (integrationId: string, id: string): Promise<ApiResponse<ExperimentScoreBreakdown>> => {
    const response = await vuserApi.get<ApiResponse<ExperimentScoreBreakdown>>(`/vuser/experiments/${id}/score`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  experimentDialogs: async (integrationId: string, id: string): Promise<ApiResponse<Dialog[]>> => {
    const response = await vuserApi.get<ApiResponse<Dialog[]>>(`/vuser/experiments/${id}/dialogs`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  experimentStatistics: async (integrationId: string, id: string): Promise<ApiResponse<ExperimentTokenStatistics>> => {
    const response = await vuserApi.get<ApiResponse<ExperimentTokenStatistics>>(`/vuser/experiments/${id}/statistics`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  /**
   * Build train/validation dataset JSON from experiment dialogs and persist for the integration (training pipeline).
   * POST /vuser/experiments/dataset?integration_id=
   */
  uploadExperimentDataset: async (
    integrationId: string,
    body: {
      experiment_ids: string[];
      history_depth?: number;
      limit_history?: boolean;
      reaction_weights?: import('../../types/reactionWeights').ReactionWeights;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> => {
    const response = await vuserApi.post<ApiResponse<Record<string, unknown>>>('/vuser/experiments/dataset', body, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  /** ML token usage prediction for dataset generation (direct response, no success/data wrapper). */
  predictDatasetGenerationTokenUsage: async (
    integrationId: string,
    body: import('../../types/datasetGenerationPreview').DatasetGenerationPredictTokenUsageRequest
  ): Promise<import('../../types/datasetGenerationPreview').DatasetGenerationPredictTokenUsageResponse> => {
    const response = await vuserApi.post<
      import('../../types/datasetGenerationPreview').DatasetGenerationPredictTokenUsageResponse
    >('/vuser/dataset_generation_predict_token_usage', body, {
      params: withIntegration(integrationId),
      headers: withAccessTokenAuth(),
    });
    return response.data;
  },

  // Chat viewer — labels & resolved evaluation (message + step level)
  chatViewerGetMessageLabel: async (
    integrationId: string,
    messageId: string
  ): Promise<ApiResponse<ChatViewerLabelResponse>> => {
    const response = await vuserApi.get<ApiResponse<ChatViewerLabelResponse>>(
      `/vuser/chat-viewer/messages/${messageId}/label`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerPutMessageLabel: async (
    integrationId: string,
    messageId: string,
    body: ChatViewerLabelUpsertBody
  ): Promise<ApiResponse<unknown>> => {
    const response = await vuserApi.put<ApiResponse<unknown>>(`/vuser/chat-viewer/messages/${messageId}/label`, body, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  chatViewerGetMessageResolvedLabel: async (
    integrationId: string,
    messageId: string
  ): Promise<ApiResponse<ChatViewerResolvedLabel>> => {
    const response = await vuserApi.get<ApiResponse<ChatViewerResolvedLabel>>(
      `/vuser/chat-viewer/messages/${messageId}/resolved-label`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerGetStepLabel: async (
    integrationId: string,
    stepId: string
  ): Promise<ApiResponse<ChatViewerLabelResponse>> => {
    const response = await vuserApi.get<ApiResponse<ChatViewerLabelResponse>>(
      `/vuser/chat-viewer/steps/${stepId}/label`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerPutStepLabel: async (
    integrationId: string,
    stepId: string,
    body: ChatViewerLabelUpsertBody
  ): Promise<ApiResponse<unknown>> => {
    const response = await vuserApi.put<ApiResponse<unknown>>(`/vuser/chat-viewer/steps/${stepId}/label`, body, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  chatViewerGetStepResolvedLabel: async (
    integrationId: string,
    stepId: string
  ): Promise<ApiResponse<ChatViewerResolvedLabel>> => {
    const response = await vuserApi.get<ApiResponse<ChatViewerResolvedLabel>>(
      `/vuser/chat-viewer/steps/${stepId}/resolved-label`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerGetStepReactions: async (
    integrationId: string,
    stepId: string
  ): Promise<ApiResponse<ChatViewerStepVirtualReaction[]>> => {
    const response = await vuserApi.get<ApiResponse<ChatViewerStepVirtualReaction[]>>(
      `/vuser/chat-viewer/steps/${stepId}/reactions`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerGetMessageStepReactions: async (
    integrationId: string,
    messageId: string
  ): Promise<ApiResponse<ChatViewerStepVirtualReaction[]>> => {
    const response = await vuserApi.get<ApiResponse<ChatViewerStepVirtualReaction[]>>(
      `/vuser/chat-viewer/messages/${messageId}/step-reactions`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerGetMessageStepLabels: async (integrationId: string, messageId: string): Promise<ApiResponse<unknown>> => {
    const response = await vuserApi.get<ApiResponse<unknown>>(`/vuser/chat-viewer/messages/${messageId}/step-labels`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  chatViewerGetDialogStepLabels: async (integrationId: string, dialogId: string): Promise<ApiResponse<unknown>> => {
    const response = await vuserApi.get<ApiResponse<unknown>>(`/vuser/chat-viewer/dialogs/${dialogId}/step-labels`, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  // RAG folders
  ragListFolders: async (integrationId: string): Promise<ApiResponse<RagFolder[]>> => {
    const response = await vuserApi.get<ApiResponse<RagFolder[]>>('/vuser/rag/folders', {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  ragCreateFolder: async (
    integrationId: string,
    body: { name: string; description?: string }
  ): Promise<ApiResponse<RagFolder>> => {
    const response = await vuserApi.post<ApiResponse<RagFolder>>('/vuser/rag/folders', body, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  ragUpdateFolder: async (
    integrationId: string,
    folderId: string,
    body: { name?: string; description?: string }
  ): Promise<ApiResponse<RagFolder>> => {
    const response = await vuserApi.put<ApiResponse<RagFolder>>(
      `/vuser/rag/folders/${encodeURIComponent(folderId)}`,
      body,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  ragDeleteFolder: async (integrationId: string, folderId: string): Promise<ApiResponse<RagDeleteFolderResponse>> => {
    const response = await vuserApi.delete<ApiResponse<RagDeleteFolderResponse>>(
      `/vuser/rag/folders/${encodeURIComponent(folderId)}`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  // RAG documents (scoped by integration_url via integration_id)
  ragPresignDocument: async (
    integrationId: string,
    body: { filename: string; mime_type: string; folder_id?: string | null }
  ): Promise<ApiResponse<RagPresignResponseData>> => {
    const response = await vuserApi.post<ApiResponse<RagPresignResponseData>>('/vuser/rag/documents/presign', body, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  /** PUT binary file to S3 presigned URL (not same-origin; uses fetch). Retries on transient transport failures. */
  ragUploadToPresignedUrl: async (uploadUrl: string, file: Blob, mimeType: string): Promise<void> => {
    const maxAttempts = 3;
    const backoffMs = [0, 450, 1200];
    let lastErr: unknown;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (backoffMs[attempt] > 0) {
        await new Promise((r) => setTimeout(r, backoffMs[attempt]));
      }
      try {
        const res = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': mimeType,
            'x-amz-acl': 'private',
          },
          body: file,
        });
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(t || `Upload failed: ${res.status} ${res.statusText}`);
        }
        return;
      } catch (e) {
        lastErr = e;
        const canRetry = attempt < maxAttempts - 1 && isLikelyTransientFetchError(e);
        if (!canRetry) throw e;
      }
    }
    throw lastErr;
  },

  ragNotifyDocumentUploaded: async (integrationId: string, docId: string): Promise<ApiResponse<RagDocument>> => {
    const response = await vuserApi.post<ApiResponse<RagDocument>>(
      `/vuser/rag/documents/${encodeURIComponent(docId)}/uploaded`,
      {},
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  ragListDocuments: async (
    integrationId: string,
    opts?: { folder_id?: string | null }
  ): Promise<ApiResponse<RagDocument[]>> => {
    const params = withIntegration(
      integrationId,
      opts?.folder_id != null && opts.folder_id !== '' ? { folder_id: opts.folder_id } : undefined
    );
    const response = await vuserApi.get<ApiResponse<RagDocument[]>>('/vuser/rag/documents', { params });
    return response.data;
  },

  ragSetDocumentUsable: async (
    integrationId: string,
    body: { doc_id: string; usable: boolean }
  ): Promise<ApiResponse<RagDocument>> => {
    const response = await vuserApi.post<ApiResponse<RagDocument>>('/vuser/rag/documents/usable', body, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  ragSetDocumentUsableByPath: async (
    integrationId: string,
    docId: string,
    usable: boolean
  ): Promise<ApiResponse<RagDocument>> => {
    const response = await vuserApi.post<ApiResponse<RagDocument>>(
      `/vuser/rag/documents/${encodeURIComponent(docId)}/usable`,
      { usable },
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  ragPatchDocumentFolder: async (
    integrationId: string,
    docId: string,
    folderId: string | null
  ): Promise<ApiResponse<RagDocument>> => {
    const response = await vuserApi.patch<ApiResponse<RagDocument>>(
      `/vuser/rag/documents/${encodeURIComponent(docId)}/folder`,
      { folder_id: folderId },
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  ragDeleteDocument: async (integrationId: string, documentId: string): Promise<ApiResponse<RagDeleteResponseData>> => {
    const response = await vuserApi.delete<ApiResponse<RagDeleteResponseData>>(
      `/vuser/rag/documents/${encodeURIComponent(documentId)}`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },
};
